import { useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import {
  AGE_GROUP_OPTIONS,
  AGE_LINK_TYPES,
  ITEM_LINK_TYPES,
  LINK_OPTIONS,
  PHONE_LINK_TYPES,
  getAgeGroupLabel,
  getLinkLabel,
  toPhoneDigits,
} from "../../utils/contentLinks";
import useCatalogSearch from "./useCatalogSearch";

/** Debounced search over active tests or packages; shows the chosen item until "Change" is clicked. */
const ItemSearch = ({ kind, selectedId, selectedLabel, onSelect, disabled, invalid }) => {
  const [open, setOpen] = useState(!selectedId);
  const [search, setSearch] = useState("");
  const { results, searching } = useCatalogSearch(kind, search, open);

  if (!open && selectedId) {
    return (
      <div className="input-group input-group-sm">
        <input
          type="text"
          className={`form-control ${invalid ? "is-invalid" : ""}`}
          value={selectedLabel || selectedId}
          readOnly
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setOpen(true)}
          disabled={disabled}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="input-group input-group-sm">
        <input
          type="text"
          className={`form-control ${invalid ? "is-invalid" : ""}`}
          placeholder={`Search ${kind}s by name...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
        />
        {selectedId && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setOpen(false)}
            disabled={disabled}
          >
            Cancel
          </button>
        )}
      </div>
      <div className="list-group mt-1" style={{ maxHeight: "200px", overflowY: "auto" }}>
        {searching && <div className="list-group-item small text-muted">Searching...</div>}
        {!searching && results.length === 0 && (
          <div className="list-group-item small text-muted">No active {kind}s found</div>
        )}
        {!searching &&
          results.map((item) => (
            <button
              key={item._id}
              type="button"
              className={`list-group-item list-group-item-action small ${
                item._id === selectedId ? "active" : ""
              }`}
              onClick={() => {
                onSelect(item._id, item.title || "");
                setOpen(false);
              }}
              disabled={disabled}
            >
              {item.title || item._id}
            </button>
          ))}
      </div>
    </div>
  );
};

/** Select over main categories (shows `name`). Keeps an unknown saved id selectable on edit. */
const CategorySelect = ({ selectedId, selectedLabel, onSelect, disabled, invalid }) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await AuthInterceptor("/admin/main-category", { method: "GET" });
      if (cancelled) return;
      setCategories(Array.isArray(response?.data?.r) ? response.data.r : []);
      setLoadingCategories(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasSelected = !selectedId || categories.some((item) => item._id === selectedId);

  return (
    <select
      className={`form-select form-select-sm ${invalid ? "is-invalid" : ""}`}
      value={selectedId || ""}
      onChange={(e) => {
        const category = categories.find((item) => item._id === e.target.value);
        onSelect(e.target.value, category?.name || "");
      }}
      disabled={disabled || loadingCategories}
    >
      <option value="" disabled>
        {loadingCategories ? "Loading categories..." : "Select category"}
      </option>
      {!hasSelected && <option value={selectedId}>{selectedLabel || selectedId}</option>}
      {categories.map((item) => (
        <option key={item._id} value={item._id}>
          {item.name}
          {item.isActive === false ? " (inactive)" : ""}
        </option>
      ))}
    </select>
  );
};

/**
 * Picks where a slider/banner redirects on tap.
 * `value` is the link object ({ type, value?, label }) or null for "No redirect";
 * `onChange` receives the next link object (or null).
 */
const RedirectPicker = ({ value, onChange, error, disabled = false, label = "Redirect" }) => {
  const type = value?.type || "";
  const linkValue = value?.value || "";
  const linkLabel = value?.label || "";

  const update = (nextValue, nextLabel = "") => onChange({ type, value: nextValue, label: nextLabel });

  const handleTypeChange = (nextType) => {
    if (!nextType) {
      onChange(null);
      return;
    }
    onChange({ type: nextType, value: "", label: "" });
  };

  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <select
        className={`form-select ${error && !type ? "is-invalid" : ""}`}
        value={type}
        onChange={(e) => handleTypeChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">No redirect</option>
        {LINK_OPTIONS.map((item) => (
          <option key={item.type} value={item.type}>
            {item.label}
          </option>
        ))}
      </select>

      {PHONE_LINK_TYPES.includes(type) && (
        <input
          type="tel"
          inputMode="numeric"
          maxLength={10}
          className={`form-control form-control-sm mt-2 ${error ? "is-invalid" : ""}`}
          placeholder="10-digit mobile number"
          value={linkValue}
          onChange={(e) => update(toPhoneDigits(e.target.value))}
          disabled={disabled}
        />
      )}

      {ITEM_LINK_TYPES.includes(type) && (
        <div className="mt-2">
          <ItemSearch
            key={type}
            kind={type}
            selectedId={linkValue}
            selectedLabel={linkLabel}
            onSelect={update}
            disabled={disabled}
            invalid={Boolean(error)}
          />
        </div>
      )}

      {type === "category" && (
        <div className="mt-2">
          <CategorySelect
            selectedId={linkValue}
            selectedLabel={linkLabel}
            onSelect={update}
            disabled={disabled}
            invalid={Boolean(error)}
          />
        </div>
      )}

      {AGE_LINK_TYPES.includes(type) && (
        <select
          className={`form-select form-select-sm mt-2 ${error ? "is-invalid" : ""}`}
          value={linkValue}
          onChange={(e) => update(e.target.value, getAgeGroupLabel(e.target.value))}
          disabled={disabled}
        >
          <option value="" disabled>
            Select age group
          </option>
          {AGE_GROUP_OPTIONS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      )}

      {type === "url" && (
        <input
          type="url"
          className={`form-control form-control-sm mt-2 ${error ? "is-invalid" : ""}`}
          placeholder="https://example.com"
          value={linkValue}
          onChange={(e) => update(e.target.value)}
          disabled={disabled}
        />
      )}

      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

/** Readable link for tables / view modals; external URLs open in a new tab. */
export const LinkValue = ({ link, truncate = false }) => {
  if (!link || !link.type) return <span className="text-muted">No redirect</span>;
  const text = getLinkLabel(link);
  const className = truncate ? "text-truncate d-inline-block align-bottom" : "text-break";
  const style = truncate ? { maxWidth: "100%" } : undefined;
  if (link.type === "url" && link.value) {
    return (
      <a
        href={link.value}
        target="_blank"
        rel="noopener noreferrer"
        className={`text-primary text-decoration-none ${className}`}
        style={style}
        title={text}
      >
        {text}
      </a>
    );
  }
  return (
    <span className={className} style={style} title={text}>
      {text}
    </span>
  );
};

export default RedirectPicker;
